import React, { useEffect, useMemo, useState } from "react";
import { api } from "../../../util/api";
import {
    Box, Paper, Typography, TableBody, TableCell,
    TableHead, TableRow, Divider,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ActionButton from "../../../component/common/ActionButton";
import TableToolbar from "../../../component/common/TableToolbar";
import { useTableSearch } from "../../../util/tableSearch";
import { notify } from "../../../util/toast";

import { API } from "../../auth/admin/api/endpoints";
import { ds } from "../../../styles/designSystem";
import PositionEditModal from "../../auth/admin/modals/PositionEditModal";
import LoadingDots from "../../../component/common/LoadingDots";
import ResizableTable from "../../../component/common/ResizableTable";

export default function HrPositionsTab() {
    const [loading, setLoading] = useState(false);
    const [positions, setPositions] = useState([]);
    // 표 안 검색 — 공통 유틸이 행 전체를 훑는다(검색 대상 컬럼을 따로 관리하지 않는다).
    const [tableQuery, setTableQuery] = useState("");
    const positionsFiltered = useTableSearch(positions, tableQuery);

    const [openEdit, setOpenEdit] = useState(false);
    const [editTarget, setEditTarget] = useState(null);

    const [sort, setSort] = useState({ key: "positionName", dir: "asc" });
    const toggleSort = (key) => setSort(s => ({ key, dir: s.key === key && s.dir === "asc" ? "desc" : "asc" }));
    const sortLabel = (key, label) => `${label}${sort.key === key ? (sort.dir === "asc" ? " ▲" : " ▼") : " ⇅"}`;
    const sortedPositions = useMemo(() => {
        return [...positionsFiltered].sort((a, b) => {
            const av = a[sort.key] ?? ""; const bv = b[sort.key] ?? "";
            const cmp = typeof av === "number" ? av - bv : String(av).localeCompare(String(bv), "ko");
            return sort.dir === "asc" ? cmp : -cmp;
        });
    }, [positionsFiltered, sort]);

    const load = async () => {
        setLoading(true);
        try {
            const data = await api.get(API.positions.list);
            setPositions(Array.isArray(data) ? data : (data?.data || []));
        } catch (e) {
            notify(e?.response?.data?.message || "직위/직급 목록 로딩 실패", "error");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <Paper sx={ds.card}>
                <Box sx={{ px: 2.5, pt: 2, pb: 1.5, display: "flex", alignItems: "center", gap: 2 }}>
                    <Typography sx={{ ...ds.typography.sectionTitle, flex: 1 }}>
                        직위/직급 목록 <span style={{ color: ds.colors.textMuted, fontWeight: 400, fontSize: 13 }}>{positions.length}개</span>
                    </Typography>
                    {/* 검색 — 동작 버튼 왼쪽, 우측 상단 고정(전 시스템 공통 배치) */}
                    <TableToolbar onSearch={setTableQuery} placeholder="직위/직급명 검색"
                        sx={{ flex: 1, mb: 0, minWidth: 200 }} />
                    <ActionButton variant="add"
                        startIcon={<AddIcon sx={{ fontSize: 15 }} />}
                        onClick={() => { setEditTarget(null); setOpenEdit(true); }}
                        label="직위/직급 추가" />
                </Box>
                <Divider />
                {loading ? (
                    <LoadingDots />
                ) : (
                    <ResizableTable size="small" sx={ds.tableSx}>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ width: 100, cursor: "pointer" }} onClick={() => toggleSort("positionId")}>{sortLabel("positionId", "ID")}</TableCell>
                                <TableCell sx={{ cursor: "pointer" }} onClick={() => toggleSort("positionName")}>{sortLabel("positionName", "직위/직급명")}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {sortedPositions.map(p => (
                                <TableRow key={p.positionId} hover sx={{ cursor: "pointer" }}
                                    onClick={() => { setEditTarget(p); setOpenEdit(true); }}>
                                    <TableCell sx={{ color: ds.colors.textMuted }}>{p.positionId}</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>{p.positionName}</TableCell>
                                </TableRow>
                            ))}
                            {sortedPositions.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={2} align="center" sx={{ py: 4, color: ds.colors.textMuted }}>직위/직급이 없습니다.</TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </ResizableTable>
                )}
            </Paper>

            <PositionEditModal
                open={openEdit} onClose={() => setOpenEdit(false)}
                initial={editTarget}
                onSaved={() => { setOpenEdit(false); load(); }}
                onDeleted={() => { setOpenEdit(false); load(); }}
            />
        </Box>
    );
}
